/**
 * Talking to the API.
 *
 * Same origin, so `credentials: 'same-origin'` is enough and there is no CORS
 * preflight on anything. The session travels as an HttpOnly cookie the browser
 * attaches itself — nothing here reads or stores a token, which is the point of
 * it being HttpOnly.
 */

import type {
  CurriculumNode,
  ExtractionFailure,
  ObjectiveStatus,
  ReachWindow,
} from '@nabu/shared';

export interface ApiError {
  error: string;
  status: number;
  /**
   * Which of several things was wrong, when the error code alone does not say.
   *
   * The reach window is the case that needs it: one `invalid_window` covers a cap
   * out of range, an unreadable time, an empty window and one that wraps past
   * midnight, and a form of three controls cannot be corrected from the word
   * "invalid". Absent on every refusal that is already specific.
   */
  reason?: string;
  /**
   * The ceiling a refused upload went over, so the message can name the number.
   *
   * "Too large" without a limit is not a correction: a learner cannot tell
   * whether to retake the photograph or split the PDF, and the two limits here
   * differ by more than a factor of two.
   */
  maxBytes?: number;
}

async function post<T>(path: string, body: unknown): Promise<T | ApiError> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });

  // Parsed before the status is checked, because the API returns its reasons in
  // the body — a bare status would lose "rate_limited" versus "invalid_number",
  // which are different things to tell somebody.
  const text = await response.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'unreadable_response', status: response.status };
  }

  if (!response.ok) {
    const failure = parsed as { error?: string; reason?: string; maxBytes?: number };
    return {
      error: failure.error ?? 'request_failed',
      status: response.status,
      ...(failure.reason ? { reason: failure.reason } : {}),
      ...(typeof failure.maxBytes === 'number' ? { maxBytes: failure.maxBytes } : {}),
    };
  }
  return parsed as T;
}

export function isError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'error' in value && 'status' in value;
}

export interface Session {
  signedIn: boolean;
  userId?: string;
  locale?: string;
}

export async function whoAmI(): Promise<Session> {
  try {
    const response = await fetch('/api/auth/me', { credentials: 'same-origin' });
    if (!response.ok) return { signedIn: false };
    return (await response.json()) as Session;
  } catch {
    // Offline, or the API is down. Not signed in is the safe answer and the app
    // still renders — a landing page must not blank out because a fetch failed.
    return { signedIn: false };
  }
}

export const startSignIn = (phone: string) => post<{ ok: true }>('/api/auth/start', { phone });

export const verifyCode = (phone: string, code: string) =>
  post<{ ok: true; isNew: boolean; learner: { name: string; locale: string } }>(
    '/api/auth/verify',
    {
      phone,
      code,
      locale: document.documentElement.lang || 'es',
      // Sent from the browser because only the browser knows it, and quiet hours
      // are meaningless without it. Captured at sign-in so the very first
      // schedule is already in the learner's own time.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

export const signOut = () => post<{ ok: true }>('/api/auth/signout', {});

/*
 * The same sign-in, by email (F-101).
 *
 * Three calls rather than two because attaching an address to an account that already
 * exists is a different act from being admitted by one — `linkEmail` is the only one
 * that needs a session, and the API will refuse it without one.
 */

/**
 * Asks for a code by email.
 *
 * The locale goes with it, unlike the phone path: the message is written by the API and
 * this is the first thing a new learner ever receives from us, so it should be in the
 * language they are reading the site in. `document.documentElement.lang` is what the
 * provider above writes, so it is the same answer the page is rendering with.
 */
export const startEmailSignIn = (email: string) =>
  post<{ ok: true }>('/api/auth/email/start', {
    email,
    locale: document.documentElement.lang || 'es',
  });

export const verifyEmailCode = (email: string, code: string) =>
  post<{ ok: true; isNew: boolean; learner: { name: string; locale: string } }>(
    '/api/auth/email/verify',
    {
      email,
      code,
      locale: document.documentElement.lang || 'es',
      // As on the phone path: only the browser knows this, and a schedule without it is
      // meaningless.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  );

/**
 * Attaches a proved address to the account already signed in.
 *
 * No locale and no timezone: this learner exists and already has both, and sending them
 * again would let a second sign-in quietly overwrite a schedule they chose.
 */
export const linkEmail = (email: string, code: string) =>
  post<{ ok: true; email: string }>('/api/auth/email/link', { email, code });

// ---- Mail: is it arriving, and do they want the part they can stop (F-106) ----

/**
 * What the API knows about mail to this learner's address.
 *
 * `suppressed` is a fact about the mailbox — a hard bounce or a complaint — and
 * `reportsOptOut` is a choice they made. They are separate fields because they are separate
 * things: clearing a bounce must not re-subscribe somebody who unsubscribed, and
 * unsubscribing must not look like a delivery problem.
 */
export interface MailState {
  /** Absent when no address is attached. There is nothing to report then. */
  email?: string;
  suppressed: boolean;
  reason?: 'hard_bounce' | 'complaint';
  /** ISO 8601, when it was recorded. */
  at?: string;
  /** The provider's own words. "550 5.1.1 user unknown" is actionable; "bouncing" is not. */
  diagnostic?: string;
  canRetry: boolean;
  reportsOptOut: boolean;
}

export const getMailState = () => get<MailState>('/api/account/mail');

export const setReportsOptOut = (reportsOptOut: boolean) =>
  post<MailState>('/api/account/mail', { reportsOptOut });

/**
 * Asks for the suppression to be lifted, which spends one bounce finding out.
 *
 * Its own call rather than part of the save, because the two are not alike: the preference
 * is free and reversible, and this one sends real mail to an address that last time refused
 * it. 409 with a `reason` when the state refuses — no address, nothing wrong, or too soon
 * after the last attempt.
 */
export const retryMail = () => post<MailState>('/api/account/mail/retry', {});

/**
 * Stops the unsubscribable mail, with no session and nothing but the token from the link.
 *
 * **Always answers 200, valid token or not**, so nobody can learn from it whether an
 * address is one of ours. That means the page cannot report failure and should not pretend
 * to: it says the request was recorded, which is true either way.
 */
export const unsubscribeByToken = (token: string) =>
  post<{ ok: true }>(`/api/mail/unsubscribe?t=${encodeURIComponent(token)}`, {});

// ---- Objectives ----

export interface Objective {
  objectiveId: string;
  title: Record<string, string>;
  description: string;
  target: { mode: 'score'; targetScore: number } | { mode: 'date'; targetDate: string };
  // The union comes from the shared domain rather than being restated here, so
  // adding a status server-side breaks the `obj.status.*` copy at compile time
  // instead of rendering a raw identifier like `researching` at a learner.
  status: ObjectiveStatus;
  createdAt: string;
  /** Absent on objectives closed before the field existed, and on live ones. */
  closedAt?: string;
  previousStatus?: ObjectiveStatus;
  /**
   * Why the last research pass did not finish, when it did not.
   *
   * A failed pass puts the objective back to `drafting`, which on its own is
   * indistinguishable from never having started one — the screen would offer the
   * button again as though nothing had happened, and a learner who pressed it
   * twice would be told twice that it takes fifteen minutes without ever being
   * told what went wrong.
   *
   * This carries *only* failures. A successful pass writes its summary to
   * `researchDetail` and clears this, so the screen never has to work out which
   * of the two it is holding.
   */
  researchError?: string;
  /** What a finished research pass produced. Never a failure. */
  researchDetail?: string;
  /**
   * Why the last curriculum build did not finish, when it did not.
   *
   * A separate field from the success summary, and that is load-bearing here in a
   * way it is not for research: a failed synthesis returns the objective to
   * `review`, which is exactly where a *successful* one leaves it too. There is no
   * status that tells the two apart, so the field has to.
   */
  synthesisError?: string;
  /** What a finished synthesis built — "44 nodes from 1 documents". Never a failure. */
  synthesisDetail?: string;
  /**
   * Which generation of nodes the objective is holding, once it holds any.
   *
   * Read here for one thing: whether a plan exists for an upload to be folded into.
   * It is the same condition `planAutoFold` in `packages/ingest/src/extract.ts` gates
   * the automatic fold on, so a screen that promised it on any other test would
   * promise something the pipeline refuses.
   */
  curriculumVersion?: string;
  /**
   * What the last successful pass added and reworded, as numbers.
   *
   * **The screen has to state one outcome and the two detail fields are prose.** An
   * upload now folds itself in and the fold writes its own questions, so what a
   * learner is owed at the end is a single sentence — "3 new topics, 12 new
   * questions" — and building it out of `synthesisDetail` and `itemsDetail` would mean
   * parsing integers back off strings two pipelines wrote for a log. These are the
   * same integers, written by the pass that knows them.
   *
   * Absent on an objective whose last pass predates F-92, and absent after a failure,
   * which is why every read of them is guarded rather than defaulted to zero: "0 new
   * topics" and "we do not know" are different things to show somebody.
   */
  addedNodes?: number;
  changedNodes?: number;
  /**
   * Present once a complete set of questions exists, naming which set.
   *
   * This is the only signal that generation finished, because generation
   * deliberately never moves the status — see `startItemGeneration`. A run that
   * failed leaves no version behind, which is what lets the next run clean up after
   * it.
   */
  itemVersion?: string;
  /** What generation produced — "180 items across 45 nodes". Never a failure. */
  itemsDetail?: string;
  /**
   * How many questions the last successful generation wrote. See `addedNodes`.
   *
   * Zero is a real answer and the common one after a fold that changed nothing: every
   * node was already current, so no question needed writing.
   *
   * **Present only while the last thing generation wrote was a finished run.** It is
   * removed on a progress write and on a failure, which is what makes "present" answer
   * "has the run finished" — generation never moves the status, so there is nothing
   * else on the row that can. See `recordOutcome` in `packages/ingest/src/items.ts`.
   */
  itemsWritten?: number;
  /**
   * When generation last wrote anything, finished or not.
   *
   * Read for one purpose: telling this run's outcome from the previous one's. A fold
   * does not touch the generation fields, so the screen has to remember where they
   * were when the fold finished and watch this move off it.
   */
  itemsAt?: string;
  /** Why generation did not finish. Never a count. */
  itemsError?: string;
  /**
   * What the objective asked for that the sources did not carry.
   *
   * Short phrases in the learner's own language, written by synthesis alongside
   * the tree. **Not an error, and not a list of things nabu got wrong** — it is
   * the one piece of a research document's provenance that a learner can act on,
   * and it exists as data precisely so it never becomes a question again. A
   * learner was once asked in earnest whether the researcher had managed to open
   * a WHO document; see D-30 and D-31.
   *
   * Declared here rather than on the shared `Objective`, like every other field
   * a pipeline writes onto the row — the API returns the stored item, so a new
   * attribute arrives without anything in between having to be widened.
   */
  gaps?: string[];
  /**
   * A *research* pass is running on an objective that already has a plan.
   *
   * The same shape as `synthesising` and present for the same reason — the status
   * must not move, at `active` because it stops the learner's questions and at
   * `review` because a failed pass would strand the curriculum on `drafting`. The
   * API takes this attribute off the row it hands over once the lock is older than
   * any pass could be, so `true` here means a pass that is still plausibly running
   * rather than debris from one that was killed.
   */
  researching?: boolean;
  /**
   * A synthesis pass is running *on an objective that is still sending*.
   *
   * **The status cannot say this, and that is the whole point of the attribute.**
   * A running objective must stay `active` at every instant or the due sweep stops
   * finding the learner and the questions stop — so a pass folding new material in
   * leaves the status alone and sets this instead. It is therefore the only thing
   * on the screen that knows a fold is in flight, and it is what the fold panel
   * watches rather than the status.
   *
   * Written by the API when the pass is started and removed by the pipeline on both
   * success and failure. Absent means nothing is running.
   */
  synthesising?: boolean;
}

/**
 * The statuses an objective can be brought back from.
 *
 * `shelved` sits alongside `done` and `abandoned` because all three are equally
 * finished as far as the screen is concerned — but it is emphatically not a
 * synonym for `abandoned`, and the copy for the three is written separately. See
 * the note on `Objective.status` in the shared domain.
 */
const CLOSED_STATUSES: readonly ObjectiveStatus[] = ['done', 'abandoned', 'shelved'];

export const isClosed = (objective: Objective) => CLOSED_STATUSES.includes(objective.status);

export async function listObjectives(): Promise<{ objectives: Objective[] } | ApiError> {
  try {
    const response = await fetch('/api/objectives', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objectives: Objective[] };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export const createObjective = (
  description: string,
  target: Objective['target'],
) => post<{ objective: Objective }>('/api/objectives', { description, target });

/** The objective being worked on, or null. There is at most one. */
export async function currentObjective(): Promise<{ objective: Objective | null } | ApiError> {
  try {
    const response = await fetch('/api/objectives/current', { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as { objective: Objective | null };
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

/** Changes the target alone. The description is settled once and never resent. */
export const updateTarget = (objectiveId: string, target: Objective['target']) =>
  post<{ target: Objective['target'] }>('/api/objectives/target', { objectiveId, target });

export const closeObjective = (objectiveId: string, status: 'done' | 'abandoned') =>
  post<{ ok: true }>('/api/objectives/close', { objectiveId, status });

/**
 * Brings a closed objective back, whether or not one is already live.
 *
 * One call for both cases deliberately: the browser does not decide whether
 * something has to be set aside first. `status` is where the objective landed —
 * it resumes what it was doing rather than restarting as a draft — and `shelved`
 * names the objective that was moved out of the way, absent when none was.
 */
export const reactivateObjective = (objectiveId: string) =>
  post<{ ok: true; objectiveId: string; status: ObjectiveStatus; shelved?: string }>(
    '/api/objectives/reactivate',
    { objectiveId },
  );

/**
 * Asks for the research pass. Answers 202, not 200.
 *
 * "Accepted and running", because the pass takes minutes and an HTTP request that
 * waited for it would be cut off at the edge long before it finished. The
 * returned status is what the objective is *now*; where it got to afterwards is
 * only ever read back from the objective itself.
 *
 * Valid from `drafting` alone, and the API refuses a second start with
 * `not_drafting` rather than starting one — the status change is the lock. That
 * refusal is the backstop, not the plan: research is the most expensive call the
 * system makes, so the screen must not offer a second start in the first place.
 */
export const startResearch = (objectiveId: string) =>
  post<{ ok: true; status: 'researching' }>('/api/objectives/research', { objectiveId });

/**
 * Asks research to go looking again, on an objective that already has a plan.
 *
 * **The most expensive thing a learner can press: $2.38 a pass, measured
 * (docs/costs.md).** Which is why it is a different call from `startResearch`
 * rather than the same one relaxed — that one is valid from `drafting` only and its
 * status change is its lock, and neither property survives being widened to an
 * objective that is running.
 *
 * The findings are *added* to the material already there: research writes each run
 * under its own prefix in the content store, and the fold that follows extends the
 * plan rather than replacing it, so node ids — and the review history hanging off
 * them — survive. Nothing about the plan changes until the learner asks for the
 * fold.
 *
 * No `status` comes back, because the objective's does not move. `researching` on
 * the objective is what says a pass is in flight. `already_running` is a refusal to
 * pay twice; `not_ready` means the objective is not somewhere a second pass can
 * start from.
 */
export const startReresearch = (objectiveId: string) =>
  post<{ ok: true; live: boolean }>('/api/objectives/research-again', { objectiveId });

/**
 * Asks for the curriculum to be built from whatever has been read so far.
 *
 * Valid from `review`, and now from `active` as well — where it *extends* the plan
 * instead of replacing it, so the topics already being learned keep their ids and
 * with them the learner's review history. That is what makes closing a gap on a
 * running objective possible at all (F-87).
 *
 * `no_material` is a different failure from `not_ready` and is kept apart
 * deliberately: one means the objective has not got there yet, the other means it
 * has and there is nothing in the content store to build from — which is a thing
 * the learner can act on by adding a page of their own. `already_running` is the
 * third: a pass is in flight, which is a reason to wait rather than to look at
 * anything.
 *
 * **The `status` in the answer is not read, and must not be.** The route reports
 * `synthesising` whatever it started, and on a running objective that is exactly
 * the status the pass must never write — reading it back would put a screen into a
 * "building" state for an objective that is, correctly, still `active`. The
 * objective row is the only place progress is read from: `status` for a
 * pre-activation build, `synthesising` for a live one.
 */
export const startSynthesis = (objectiveId: string) =>
  post<{ ok: true }>('/api/objectives/synthesise', { objectiveId });

/**
 * Starts writing the questions for the curriculum.
 *
 * Unlike research and synthesis this leaves the status alone, so there is no
 * `status` in the answer to read progress from. A Message Batch is minutes at best
 * and up to a day by contract, and an in-progress status held that long would lock
 * the objective out of everything else — so `itemVersion` appearing on the
 * objective is what says it finished.
 */
export const startItemGeneration = (objectiveId: string) =>
  post<{ ok: true }>('/api/objectives/items', { objectiveId });

/**
 * "Start" — the word that turns a plan into something that will interrupt you.
 *
 * Deliberately its own act rather than the end of building the curriculum. It is
 * also where the learner's memory begins to exist: every item they will be asked
 * gets a review state here, which is what the due sweep looks for.
 */
export const activateObjective = (objectiveId: string) =>
  post<{ ok: true; items: number; nodes: number }>('/api/objectives/activate', { objectiveId });

export interface ResearchDocument {
  key: string;
  title: string;
  language: string;
  producedAt: string;
  origin: string;
  body: string;
  truncated: boolean;
  characters: number;
}

/**
 * What research found, so it can be read before a curriculum is built from it.
 *
 * Not part of `listSources`: a research pass creates no source row, because nothing
 * uploaded it. The two arrive at the same content store by different routes and are
 * read by different calls.
 */
export const getResearch = (objectiveId: string) =>
  get<{ documents: ResearchDocument[] }>(
    `/api/research?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

// ---- Sources ----

export interface Source {
  sourceId: string;
  objectiveId: string;
  kind: string;
  title?: string;
  status: 'pending' | 'extracted' | 'failed';
  /** What a successful extraction produced. Absent on a failure. */
  detail?: string;
  /** Why it failed, in English, for the logs. Absent on a success. */
  error?: string;
  /** The same failure as a code this interface translates. */
  reason?: ExtractionFailure;
}

export interface ExtractedContent {
  title: string;
  language: string;
  method: string;
  producedAt: string;
  body: string;
  truncated: boolean;
}

async function get<T>(path: string): Promise<T | ApiError> {
  try {
    const response = await fetch(path, { credentials: 'same-origin' });
    const parsed = (await response.json()) as unknown;
    if (!response.ok) {
      return { error: (parsed as { error?: string }).error ?? 'request_failed', status: response.status };
    }
    return parsed as T;
  } catch {
    return { error: 'unreachable', status: 0 };
  }
}

export const listSources = (objectiveId: string) =>
  get<{ sources: Source[] }>(`/api/sources?objectiveId=${encodeURIComponent(objectiveId)}`);

/**
 * Read again, or take it away. A failure with neither is litter in somebody's own
 * list of material, which is what these two exist to stop being true.
 */
export const retrySource = (objectiveId: string, sourceId: string) =>
  post<{ ok: true }>('/api/sources/retry', { objectiveId, sourceId });

export const deleteSource = (objectiveId: string, sourceId: string) =>
  post<{ ok: true }>('/api/sources/delete', { objectiveId, sourceId });

export const readExtracted = (objectiveId: string, sourceId: string) =>
  get<{ content: ExtractedContent }>(
    `/api/sources/content?objectiveId=${encodeURIComponent(objectiveId)}&sourceId=${encodeURIComponent(sourceId)}`,
  );

export const presignUpload = (
  objectiveId: string,
  contentType: string,
  title: string,
  bytes: number,
) =>
  post<{ sourceId: string; url: string; key: string; maxBytes: number }>('/api/uploads', {
    objectiveId,
    contentType,
    title,
    bytes,
  });

// ---- The curriculum ----

/**
 * The whole node tree for one objective, already ordered by the API.
 *
 * Ordered *there*, not here: `gsi3` carries the order zero-padded so that a
 * lexicographic sort matches the learner's chosen order, and re-sorting the
 * result in the browser would be a second opinion about ordering that could
 * quietly disagree with the one the rest of the system uses.
 *
 * An empty `nodes` is the ordinary answer before anything has been built, not an
 * error — nothing is wrong with an objective that has no curriculum yet.
 */
export const getCurriculum = (objectiveId: string) =>
  get<{ nodes: CurriculumNode[]; curriculumVersion?: string }>(
    `/api/curriculum?objectiveId=${encodeURIComponent(objectiveId)}`,
  );

/**
 * Turning one subject on or off.
 *
 * One node per call rather than the whole selection, matching the endpoint: a
 * checkbox is one decision, and posting the entire set on every tap would make two
 * quick taps race to overwrite each other's *other* subjects, not just their own.
 *
 * The answer repeats `active` rather than being a bare `{ ok: true }`, and the
 * browser believes that over what it asked for — it is the only thing that knows
 * what is actually stored. `not_top_level` is a refusal by design: selection exists
 * only on top-level subjects, and a nested topic follows its parent.
 */
export const setNodeActive = (objectiveId: string, nodeId: string, active: boolean) =>
  post<{ ok: true; active: boolean }>('/api/curriculum/active', { objectiveId, nodeId, active });

// ---- Where the learner is ----

/**
 * What is known about a set of questions, in counts first and ratios second.
 *
 * Every ratio here arrives with its own denominator, and that is the whole contract
 * of this shape rather than a nicety: `recall` on its own is 100% for a learner who
 * has answered one question out of five hundred, and is true, and is a lie by
 * omission the moment it is rendered without `answered` beside it.
 */
export interface Evidence {
  /** Questions in scope. Explanations are excluded — nobody answers one. */
  questions: number;
  /** Never asked, so there is no evidence either way. Not the same as "got wrong". */
  untested: number;
  /** Asked, and either failed last time or forgotten at least once since. */
  struggling: number;
  /** Asked, and neither failed last time nor ever forgotten. */
  holding: number;
  /** The denominator of `recall`, and the only honest way to read it. */
  answered: number;
  due: number;
  /** Mean recall probability across answered items alone. Null when none are. */
  recall: number | null;
}

export interface SubjectProgress {
  nodeId: string;
  title: Record<string, string>;
  order: number;
  weight: number;
  /**
   * Whether the subject is still ticked on the plan.
   *
   * False does not mean it is being left alone: unticking after Start does not remove
   * the review states that were already written, so its questions keep arriving. The
   * panel says so rather than hiding the subject and disagreeing with the tutor.
   */
  active: boolean;
  /** Null when no questions were written for this subject, which is not zero. */
  mastery: number | null;
  evidence: Evidence;
}

/**
 * Today, in the terms the sending sweep actually decides in.
 *
 * **`dailyLimit` counts questions, and only questions.** Briefings and reminders are
 * sent outside it — a briefing because it teaches rather than asks, a reminder
 * because it repeats a question already asked and is bounded by the escalation
 * instead. So `sentToday` is a count of questions and the two never drift: anything
 * else arriving on the learner's phone is deliberately not in this shape.
 *
 * It is a ceiling and not a forecast, and since one question is outstanding at a time
 * it is a ceiling reality rarely reaches — which is why `answeredToday` is part of
 * this shape rather than something the screen infers.
 */
export interface Load {
  dailyLimit: number | null;
  /** Null when we do not know the learner's zone, so there is no local day to count. */
  sentToday: number | null;
  answeredToday: number;
  dueNow: number;
  awaitingAnswer: boolean;
  paused: boolean;
  pausedReason?: 'asked' | 'no_answer';
  timezone: string | null;
  localDay: string | null;
  reason: string;
  nextSendAt: string | null;
}

/**
 * Whether the objective is on track, when that question means anything.
 *
 * A score-targeted objective has no date and so cannot be behind one. The union
 * carries no verdict on that branch deliberately — answering "on track" for it would
 * be congratulating somebody for a deadline they never set.
 */
export type Pace =
  | { mode: 'score'; targetScore: number }
  | {
      mode: 'date';
      targetDate: string;
      /** False is trustworthy; true means only "not provably impossible". */
      feasible: boolean;
      requiredPerDay: number;
      capacityPerDay: number;
      daysLeft: number;
    };

export interface ProgressView {
  objectiveId: string;
  target: Objective['target'];
  standard: number;
  overall: Evidence;
  /** Weighted by what each subject is worth in the plan. Null when nothing measurable. */
  mastery: number | null;
  subjects: SubjectProgress[];
  explanations: number;
  unplaced: number;
  subjectsWithoutQuestions: number;
  load: Load;
  pace: Pace;
  /** True when the read stopped early, making every count above a floor. */
  partial: boolean;
  at: string;
}

/**
 * Where the learner is on the objective that is actually running.
 *
 * No objective id in the request: progress is a statement about the one being
 * taught, and it comes from the session rather than from anything the browser sends.
 * `progress: null` is the ordinary answer when nothing is running — review state does
 * not exist before Start, and a plan awaiting approval is not a plan at nought per
 * cent.
 */
export const getProgress = () => get<{ progress: ProgressView | null }>('/api/progress');

/**
 * The review sheet, fetched and handed to the browser as a file.
 *
 * Fetched rather than navigated to. Pointing the window at `/api/sheet` would be
 * three lines shorter and would render the refusals — "nothing answered yet", "the
 * rewrite did not come back" — as raw JSON in a blank tab, which is the API's error
 * shape shown to a person. Going through `fetch` keeps them as something the panel
 * can say in the learner's own language, and keeps the button's in-flight state
 * honest: a navigation reports nothing about whether the work finished.
 *
 * The filename comes from the response rather than being composed here, so the
 * server owns it in one place — and the object URL is revoked afterwards, because a
 * learner who downloads this every morning would otherwise accumulate a blob per
 * download for the life of the tab.
 */
export async function downloadSheet(): Promise<{ ok: true } | ApiError> {
  let response: Response;
  try {
    response = await fetch('/api/sheet', { credentials: 'same-origin' });
  } catch {
    // Offline, or the API is unreachable. A thrown fetch and a refused request are
    // the same thing to somebody looking at a button that did nothing.
    return { error: 'unreachable', status: 0 };
  }

  if (!response.ok) {
    const failure = (await response.json().catch(() => ({}))) as { error?: string };
    return { error: failure.error ?? 'request_failed', status: response.status };
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const named = /filename="([^"]+)"/.exec(disposition);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = named?.[1] ?? 'nabu.pdf';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return { ok: true };
}

// ---- Being reached ----

export interface WindowState {
  window: ReachWindow;
  /** Whether sending is stopped. See `setPaused`. */
  paused: boolean;
  /**
   * Why it stopped, when it has.
   *
   * `asked` is a setting the learner chose; `no_answer` is the escalation giving up
   * after three ignored reminders. The screen must say different things for each — one
   * is a decision, the other is something that happened to them — and a single
   * "paused" message for both would tell somebody who never touched it that they had.
   */
  pausedReason?: 'asked' | 'no_answer';
  /**
   * The learner's IANA zone, absent when we do not have one for them.
   *
   * Deliberately not filled in with a plausible default by the API: reachable hours
   * read in the wrong zone are worse than none, because `08:00`–`21:00` taken as UTC
   * is the middle of a Costa Rican night. The screen says so instead of showing
   * hours that will not be honoured.
   */
  timezone?: string;
  /** False until a window has actually been saved, which is when the defaults are labelled as such. */
  configured: boolean;
}

export const getWindow = () => get<WindowState>('/api/window');

/**
 * Stops or restarts the messages, without closing anything.
 *
 * Distinct from finishing an objective, which is the whole point: a busy week should
 * not cost a learner their progress. Everything is kept — the objective stays active
 * and every review state survives — and only the sending stops.
 */
export const setPaused = (paused: boolean) =>
  post<{ ok: true; paused: boolean }>('/api/pause', { paused });

// ---- Leaving ----

/** What the deletion destroyed, so the last thing the learner sees is a count. */
export interface DeletionReport {
  objectives: number;
  nodes: number;
  items: number;
  reviews: number;
  learnerRows: number;
  messages: number;
  pointers: number;
  /** The `EMAIL#` row that granted sign-in, if they had proved an address (F-101). */
  emailPointers: number;
  /** What we knew about mail to that address — bounces and the provider's words (F-106). */
  mailStatusRows: number;
  objectVersions: number;
  usageCountersKept: number;
}

/**
 * The literal word the API requires before it destroys anything.
 *
 * Not a password — the session is what authorises this. It is here so the one
 * irreversible endpoint in the system cannot be reached by a request that meant to
 * reach another one, and it is exported so the browser and the API agree on it in
 * one place rather than two string literals that could drift apart into a delete
 * button that silently stopped working.
 */
export const DELETE_CONFIRMATION = 'delete-everything';

/**
 * Erases the account and everything in it. There is no undo and no copy.
 *
 * The response clears the session cookie, so the browser is signed out by the same
 * request that destroyed the account — a signed JWT cannot be revoked, and a
 * session still presenting a deleted learner's id would let the next request create
 * an objective under it.
 */
export const deleteAccount = () =>
  post<{ ok: true; deleted: DeletionReport }>('/api/account/delete', {
    confirm: DELETE_CONFIRMATION,
  });

/**
 * Saves the whole window at once.
 *
 * All three settings together rather than one at a time, matching the endpoint: they
 * only mean something as a set, and two quick taps sending partial writes would
 * leave a schedule neither of them chose. A refusal comes back as `invalid_window`
 * with `reason` naming which part was wrong.
 */
export const saveWindow = (window: ReachWindow) =>
  post<{ ok: true; window: ReachWindow; timezone?: string }>('/api/window', window);

// ---- The conversation (F-100) ----

export interface ChatMessage {
  /** Unique within the thread, and stable. Used as the React key. */
  id: string;
  direction: 'in' | 'out';
  /**
   * When it happened, as the server stored it.
   *
   * **May not be a parseable instant.** A message that arrived without a readable
   * timestamp is filed under the literal word `unknown`, deliberately, so that a
   * redelivery lands on the same row. Anything rendering this has to survive that
   * rather than assuming a date — see `Chat.tsx`.
   */
  at: string;
  /** Marked as it was sent: a question, a briefing, or a reply to the learner. */
  text: string;
  purpose?: string;
  /** Which transport carried it. Absent means WhatsApp. */
  via?: string;
}

export interface ChatPage {
  /** Oldest first, so the thread reads downwards. */
  messages: ChatMessage[];
  /** Where the older messages continue, when there are any. */
  olderCursor?: string;
}

/**
 * A page of the thread, newest by default and older with a cursor.
 *
 * No learner id: the thread belongs to whoever the cookie says. The cursor walks
 * *backwards* through the history, which is why it is not called `next` — a screen
 * that read it as "later" would page into the future and never load its own past.
 */
export const getChat = (options: { cursor?: string; limit?: number } = {}) => {
  const query = new URLSearchParams();
  if (options.cursor) query.set('cursor', options.cursor);
  if (options.limit) query.set('limit', String(options.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return get<ChatPage>(`/api/chat${suffix}`);
};

/**
 * Says something, and it is graded exactly as a WhatsApp reply is.
 *
 * **The id and the timestamp are generated by the caller and kept across a retry.**
 * They are what the server files the message under, and a second attempt that replays
 * both is refused as already handled instead of being marked a second time — two
 * model calls, two corrections, and a memory model advanced twice for one answer. A
 * server-minted id could not do that, because a failed request has no response to
 * remember.
 *
 * Nothing comes back but what happened to the message. The reply is read by the next
 * `getChat`, so there is one source for the thread and no way for what the learner
 * sees now to disagree with what a reload shows.
 */
export const sendChatMessage = (message: { text: string; messageId: string; sentAt: string }) =>
  post<{ ok: true; outcome: string }>('/api/chat', message);
// ---- Notifications (F-102) ----

export interface PushState {
  /**
   * Whether the service has a VAPID key pair at all.
   *
   * A separate field rather than the absence of `publicKey`, because the two are
   * different things to say: "notifications are not set up on this service yet" is a
   * state the screen explains and the learner can do nothing about, and it must not
   * render as an error. The route answers 200 either way for the same reason
   * `/api/auth/me` answers 200 with `signedIn: false`.
   */
  configured: boolean;
  publicKey?: string;
  /**
   * How many browsers this learner has registered, across every device.
   *
   * Not the same question as "is this browser subscribed", which only the browser can
   * answer. Both are shown, because the state where they disagree — this browser thinks
   * it is subscribed and the server has pruned the row — is exactly the one in which a
   * learner is told notifications are on and receives none.
   */
  devices: number;
}

export const getPushState = async (): Promise<PushState | ApiError> => {
  try {
    const response = await fetch('/api/push', { credentials: 'same-origin' });
    const parsed = (await response.json()) as PushState & { error?: string };
    if (!response.ok) return { error: parsed.error ?? 'request_failed', status: response.status };
    return parsed;
  } catch {
    // Offline, or the API is down. Reported as an error rather than as "not configured":
    // telling a learner notifications are unavailable on this service because their train
    // went into a tunnel is a lie they would act on by not trying again.
    return { error: 'offline', status: 0 };
  }
};

export const subscribePush = (subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  locale: string;
}) => post<{ ok: true; devices: number }>('/api/push/subscribe', subscription);

export const unsubscribePush = (endpoint: string) =>
  post<{ ok: true }>('/api/push/unsubscribe', { endpoint });

/**
 * Sends the learner a message by whatever transport they are currently on.
 *
 * Deliberately not "send me a push". It goes through the same queue and the same
 * dispatcher every real message goes through, so what arrives is the honest answer to
 * "will nabu reach me" — which for somebody still on WhatsApp is a WhatsApp. The copy
 * beside the button says so before it is pressed.
 */
export const sendTestMessage = () => post<{ ok: true }>('/api/push/test', {});
